/**
 * Advanced Audio Effects
 * Full suite of professional audio effects for String Sauce DAW
 * 
 * Studio Essentials: Reverb, Delay, Compression, EQ
 * Creative Effects: Chorus, Flanger, Phaser, Distortion
 */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import { Switch } from '../../ui/switch';
import {
  Waves,
  Clock,
  Gauge,
  Sliders,
  Sparkles,
  Zap,
  Wind,
  Radio,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Save,
  FolderOpen,
  Crown,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';

// Effect presets
const EFFECT_PRESETS = {
  reverb: {
    'Small Room': { mix: 0.2, decay: 0.3, predelay: 10, damping: 0.5 },
    'Large Hall': { mix: 0.4, decay: 0.8, predelay: 30, damping: 0.3 },
    'Cathedral': { mix: 0.5, decay: 0.95, predelay: 50, damping: 0.2 },
    'Plate': { mix: 0.35, decay: 0.6, predelay: 5, damping: 0.7 },
    'Spring': { mix: 0.3, decay: 0.4, predelay: 0, damping: 0.6 },
  },
  delay: {
    'Slapback': { mix: 0.3, time: 80, feedback: 0.1, sync: false },
    'Quarter Note': { mix: 0.25, time: 500, feedback: 0.3, sync: true },
    'Dotted Eighth': { mix: 0.3, time: 375, feedback: 0.35, sync: true },
    'Ping Pong': { mix: 0.35, time: 250, feedback: 0.4, sync: true, pingPong: true },
    'Ambient': { mix: 0.4, time: 800, feedback: 0.5, sync: false },
  },
  compression: {
    'Gentle': { threshold: -20, ratio: 2, attack: 20, release: 200, makeup: 2 },
    'Punchy': { threshold: -15, ratio: 4, attack: 5, release: 100, makeup: 4 },
    'Aggressive': { threshold: -10, ratio: 8, attack: 1, release: 50, makeup: 6 },
    'Bus Glue': { threshold: -25, ratio: 2, attack: 30, release: 300, makeup: 2 },
    'Limiter': { threshold: -3, ratio: 20, attack: 0.5, release: 50, makeup: 0 },
  },
  chorus: {
    'Subtle': { mix: 0.2, rate: 0.5, depth: 0.3, voices: 2 },
    'Warm': { mix: 0.35, rate: 0.8, depth: 0.5, voices: 3 },
    'Wide': { mix: 0.4, rate: 1.2, depth: 0.6, voices: 4 },
    '80s Synth': { mix: 0.5, rate: 2, depth: 0.7, voices: 4 },
  },
  flanger: {
    'Jet': { mix: 0.4, rate: 0.2, depth: 0.8, feedback: 0.7 },
    'Subtle Sweep': { mix: 0.25, rate: 0.1, depth: 0.4, feedback: 0.3 },
    'Metallic': { mix: 0.5, rate: 0.5, depth: 0.9, feedback: 0.85 },
  },
  phaser: {
    'Classic': { mix: 0.4, rate: 0.5, depth: 0.6, stages: 4, feedback: 0.5 },
    'Deep Sweep': { mix: 0.5, rate: 0.2, depth: 0.9, stages: 6, feedback: 0.6 },
    'Fast & Subtle': { mix: 0.3, rate: 2, depth: 0.3, stages: 4, feedback: 0.3 },
  },
  distortion: {
    'Warm Overdrive': { mix: 0.6, drive: 0.3, tone: 0.5, output: 0.7 },
    'Crunch': { mix: 0.7, drive: 0.5, tone: 0.6, output: 0.6 },
    'Heavy Distortion': { mix: 0.8, drive: 0.8, tone: 0.4, output: 0.5 },
    'Fuzz': { mix: 0.9, drive: 0.95, tone: 0.3, output: 0.4 },
  },
};

// Default effect values
const DEFAULT_EFFECTS = {
  reverb: { enabled: false, mix: 0.3, decay: 0.5, predelay: 20, damping: 0.5 },
  delay: { enabled: false, mix: 0.25, time: 375, feedback: 0.3, sync: true, pingPong: false },
  compression: { enabled: false, threshold: -20, ratio: 4, attack: 10, release: 150, makeup: 3 },
  eq: { enabled: true, low: 0, lowMid: 0, highMid: 0, high: 0, lowFreq: 100, highFreq: 8000 },
  chorus: { enabled: false, mix: 0.3, rate: 1, depth: 0.5, voices: 2 },
  flanger: { enabled: false, mix: 0.3, rate: 0.3, depth: 0.5, feedback: 0.5 },
  phaser: { enabled: false, mix: 0.4, rate: 0.5, depth: 0.6, stages: 4, feedback: 0.5 },
  distortion: { enabled: false, mix: 0.5, drive: 0.5, tone: 0.5, output: 0.7 },
};

// Effect section component
const EffectSection = ({ 
  title, 
  icon: Icon, 
  color, 
  enabled, 
  onToggle, 
  presets,
  currentPreset,
  onPresetSelect,
  isPremium,
  isLocked,
  children 
}) => {
  const [isExpanded, setIsExpanded] = useState(enabled);

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${
      enabled ? `border-${color}-500/30 bg-${color}-500/5` : 'border-zinc-800 bg-zinc-900/50'
    }`}>
      {/* Header */}
      <div 
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-zinc-800/30"
        onClick={() => !isLocked && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            enabled ? `bg-${color}-500/20` : 'bg-zinc-800'
          }`}>
            <Icon className={`w-4 h-4 ${enabled ? `text-${color}-400` : 'text-zinc-500'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className={`font-medium text-sm ${enabled ? 'text-white' : 'text-zinc-400'}`}>
                {title}
              </span>
              {isPremium && (
                <span className="px-1.5 py-0.5 text-[10px] bg-amber-500/20 text-amber-400 rounded">
                  PRO
                </span>
              )}
              {isLocked && <Lock className="w-3 h-3 text-zinc-500" />}
            </div>
            {currentPreset && (
              <span className="text-xs text-zinc-500">{currentPreset}</span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {!isLocked && (
            <Switch
              checked={enabled}
              onCheckedChange={onToggle}
              onClick={(e) => e.stopPropagation()}
              className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-orange-500 data-[state=checked]:to-amber-500"
            />
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-zinc-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-zinc-500" />
          )}
        </div>
      </div>

      {/* Content */}
      <AnimatePresence>
        {isExpanded && !isLocked && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 space-y-4 border-t border-zinc-800/50">
              {/* Presets */}
              {presets && Object.keys(presets).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {Object.keys(presets).map((preset) => (
                    <button
                      key={preset}
                      onClick={() => onPresetSelect?.(preset)}
                      className={`px-2 py-1 text-xs rounded-lg transition-all ${
                        currentPreset === preset
                          ? `bg-${color}-500/20 text-${color}-400 border border-${color}-500/50`
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-transparent'
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Controls */}
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Slider with label
const EffectSlider = ({ label, value, onChange, min = 0, max = 100, step = 1, unit = '%', color = 'orange' }) => (
  <div className="space-y-1.5">
    <div className="flex justify-between text-xs">
      <span className="text-zinc-400">{label}</span>
      <span className={`font-mono text-${color}-400`}>
        {typeof value === 'number' ? (unit === 'dB' ? (value > 0 ? '+' : '') + value.toFixed(1) : Math.round(value)) : value}{unit}
      </span>
    </div>
    <Slider
      value={[typeof value === 'number' ? value : 0]}
      onValueChange={([v]) => onChange(v)}
      min={min}
      max={max}
      step={step}
      className="w-full"
    />
  </div>
);

export const AdvancedEffects = ({
  effects = DEFAULT_EFFECTS,
  onChange,
  isPro = false,
  onUpgrade,
}) => {
  const [activePresets, setActivePresets] = useState({});

  const updateEffect = useCallback((effectType, updates) => {
    onChange?.({
      ...effects,
      [effectType]: { ...effects[effectType], ...updates }
    });
  }, [effects, onChange]);

  const selectPreset = useCallback((effectType, presetName) => {
    const preset = EFFECT_PRESETS[effectType]?.[presetName];
    if (preset) {
      updateEffect(effectType, { ...preset, enabled: true });
      setActivePresets(prev => ({ ...prev, [effectType]: presetName }));
      toast.success(`Applied "${presetName}" preset`);
    }
  }, [updateEffect]);

  const resetEffect = useCallback((effectType) => {
    updateEffect(effectType, DEFAULT_EFFECTS[effectType]);
    setActivePresets(prev => ({ ...prev, [effectType]: null }));
  }, [updateEffect]);

  const resetAll = useCallback(() => {
    onChange?.(DEFAULT_EFFECTS);
    setActivePresets({});
    toast.info('All effects reset');
  }, [onChange]);

  // Check if creative effects are locked (require Pro)
  const isCreativeLocked = !isPro;

  return (
    <div className="space-y-4" data-testid="advanced-effects">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-400" />
          Advanced Effects
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={resetAll}
          className="text-xs text-zinc-400 hover:text-white"
        >
          <RotateCcw className="w-3 h-3 mr-1" />
          Reset All
        </Button>
      </div>

      {/* Studio Essentials */}
      <div className="space-y-3">
        <span className="text-xs uppercase tracking-wider text-zinc-500">Studio Essentials</span>
        
        {/* Reverb */}
        <EffectSection
          title="Reverb"
          icon={Waves}
          color="cyan"
          enabled={effects.reverb?.enabled}
          onToggle={(v) => updateEffect('reverb', { enabled: v })}
          presets={EFFECT_PRESETS.reverb}
          currentPreset={activePresets.reverb}
          onPresetSelect={(p) => selectPreset('reverb', p)}
        >
          <EffectSlider
            label="Mix"
            value={effects.reverb?.mix * 100}
            onChange={(v) => updateEffect('reverb', { mix: v / 100 })}
            color="cyan"
          />
          <EffectSlider
            label="Decay"
            value={effects.reverb?.decay * 100}
            onChange={(v) => updateEffect('reverb', { decay: v / 100 })}
            color="cyan"
          />
          <EffectSlider
            label="Pre-Delay"
            value={effects.reverb?.predelay}
            onChange={(v) => updateEffect('reverb', { predelay: v })}
            max={100}
            unit="ms"
            color="cyan"
          />
          <EffectSlider
            label="Damping"
            value={effects.reverb?.damping * 100}
            onChange={(v) => updateEffect('reverb', { damping: v / 100 })}
            color="cyan"
          />
        </EffectSection>

        {/* Delay */}
        <EffectSection
          title="Delay"
          icon={Clock}
          color="amber"
          enabled={effects.delay?.enabled}
          onToggle={(v) => updateEffect('delay', { enabled: v })}
          presets={EFFECT_PRESETS.delay}
          currentPreset={activePresets.delay}
          onPresetSelect={(p) => selectPreset('delay', p)}
        >
          <EffectSlider
            label="Mix"
            value={effects.delay?.mix * 100}
            onChange={(v) => updateEffect('delay', { mix: v / 100 })}
            color="amber"
          />
          <EffectSlider
            label="Time"
            value={effects.delay?.time}
            onChange={(v) => updateEffect('delay', { time: v })}
            max={2000}
            unit="ms"
            color="amber"
          />
          <EffectSlider
            label="Feedback"
            value={effects.delay?.feedback * 100}
            onChange={(v) => updateEffect('delay', { feedback: v / 100 })}
            color="amber"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-400">Sync to BPM</span>
            <Switch
              checked={effects.delay?.sync}
              onCheckedChange={(v) => updateEffect('delay', { sync: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-400">Ping Pong</span>
            <Switch
              checked={effects.delay?.pingPong}
              onCheckedChange={(v) => updateEffect('delay', { pingPong: v })}
            />
          </div>
        </EffectSection>

        {/* Compression */}
        <EffectSection
          title="Compression"
          icon={Gauge}
          color="violet"
          enabled={effects.compression?.enabled}
          onToggle={(v) => updateEffect('compression', { enabled: v })}
          presets={EFFECT_PRESETS.compression}
          currentPreset={activePresets.compression}
          onPresetSelect={(p) => selectPreset('compression', p)}
        >
          <EffectSlider
            label="Threshold"
            value={effects.compression?.threshold}
            onChange={(v) => updateEffect('compression', { threshold: v })}
            min={-60}
            max={0}
            unit="dB"
            color="violet"
          />
          <EffectSlider
            label="Ratio"
            value={effects.compression?.ratio}
            onChange={(v) => updateEffect('compression', { ratio: v })}
            min={1}
            max={20}
            step={0.5}
            unit=":1"
            color="violet"
          />
          <EffectSlider
            label="Attack"
            value={effects.compression?.attack}
            onChange={(v) => updateEffect('compression', { attack: v })}
            max={100}
            unit="ms"
            color="violet"
          />
          <EffectSlider
            label="Release"
            value={effects.compression?.release}
            onChange={(v) => updateEffect('compression', { release: v })}
            max={1000}
            unit="ms"
            color="violet"
          />
          <EffectSlider
            label="Makeup Gain"
            value={effects.compression?.makeup}
            onChange={(v) => updateEffect('compression', { makeup: v })}
            max={24}
            unit="dB"
            color="violet"
          />
        </EffectSection>

        {/* 4-Band EQ */}
        <EffectSection
          title="4-Band EQ"
          icon={Sliders}
          color="emerald"
          enabled={effects.eq?.enabled}
          onToggle={(v) => updateEffect('eq', { enabled: v })}
        >
          <EffectSlider
            label="Low (100Hz)"
            value={effects.eq?.low}
            onChange={(v) => updateEffect('eq', { low: v })}
            min={-12}
            max={12}
            step={0.5}
            unit="dB"
            color="emerald"
          />
          <EffectSlider
            label="Low-Mid (500Hz)"
            value={effects.eq?.lowMid}
            onChange={(v) => updateEffect('eq', { lowMid: v })}
            min={-12}
            max={12}
            step={0.5}
            unit="dB"
            color="emerald"
          />
          <EffectSlider
            label="High-Mid (2kHz)"
            value={effects.eq?.highMid}
            onChange={(v) => updateEffect('eq', { highMid: v })}
            min={-12}
            max={12}
            step={0.5}
            unit="dB"
            color="emerald"
          />
          <EffectSlider
            label="High (8kHz)"
            value={effects.eq?.high}
            onChange={(v) => updateEffect('eq', { high: v })}
            min={-12}
            max={12}
            step={0.5}
            unit="dB"
            color="emerald"
          />
        </EffectSection>
      </div>

      {/* Creative Effects */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider text-zinc-500">Creative Effects</span>
          {isCreativeLocked && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onUpgrade}
              className="text-xs text-amber-400 hover:text-amber-300"
            >
              <Crown className="w-3 h-3 mr-1" />
              Unlock with Pro
            </Button>
          )}
        </div>

        {/* Chorus */}
        <EffectSection
          title="Chorus"
          icon={Sparkles}
          color="pink"
          enabled={effects.chorus?.enabled}
          onToggle={(v) => updateEffect('chorus', { enabled: v })}
          presets={EFFECT_PRESETS.chorus}
          currentPreset={activePresets.chorus}
          onPresetSelect={(p) => selectPreset('chorus', p)}
          isPremium
          isLocked={isCreativeLocked}
        >
          <EffectSlider
            label="Mix"
            value={effects.chorus?.mix * 100}
            onChange={(v) => updateEffect('chorus', { mix: v / 100 })}
            color="pink"
          />
          <EffectSlider
            label="Rate"
            value={effects.chorus?.rate}
            onChange={(v) => updateEffect('chorus', { rate: v })}
            min={0.1}
            max={5}
            step={0.1}
            unit="Hz"
            color="pink"
          />
          <EffectSlider
            label="Depth"
            value={effects.chorus?.depth * 100}
            onChange={(v) => updateEffect('chorus', { depth: v / 100 })}
            color="pink"
          />
          <EffectSlider
            label="Voices"
            value={effects.chorus?.voices}
            onChange={(v) => updateEffect('chorus', { voices: Math.round(v) })}
            min={2}
            max={6}
            step={1}
            unit=""
            color="pink"
          />
        </EffectSection>

        {/* Flanger */}
        <EffectSection
          title="Flanger"
          icon={Wind}
          color="teal"
          enabled={effects.flanger?.enabled}
          onToggle={(v) => updateEffect('flanger', { enabled: v })}
          presets={EFFECT_PRESETS.flanger}
          currentPreset={activePresets.flanger}
          onPresetSelect={(p) => selectPreset('flanger', p)}
          isPremium
          isLocked={isCreativeLocked}
        >
          <EffectSlider
            label="Mix"
            value={effects.flanger?.mix * 100}
            onChange={(v) => updateEffect('flanger', { mix: v / 100 })}
            color="teal"
          />
          <EffectSlider
            label="Rate"
            value={effects.flanger?.rate}
            onChange={(v) => updateEffect('flanger', { rate: v })}
            min={0.01}
            max={2}
            step={0.01}
            unit="Hz"
            color="teal"
          />
          <EffectSlider
            label="Depth"
            value={effects.flanger?.depth * 100}
            onChange={(v) => updateEffect('flanger', { depth: v / 100 })}
            color="teal"
          />
          <EffectSlider
            label="Feedback"
            value={effects.flanger?.feedback * 100}
            onChange={(v) => updateEffect('flanger', { feedback: v / 100 })}
            color="teal"
          />
        </EffectSection>

        {/* Phaser */}
        <EffectSection
          title="Phaser"
          icon={Radio}
          color="indigo"
          enabled={effects.phaser?.enabled}
          onToggle={(v) => updateEffect('phaser', { enabled: v })}
          presets={EFFECT_PRESETS.phaser}
          currentPreset={activePresets.phaser}
          onPresetSelect={(p) => selectPreset('phaser', p)}
          isPremium
          isLocked={isCreativeLocked}
        >
          <EffectSlider
            label="Mix"
            value={effects.phaser?.mix * 100}
            onChange={(v) => updateEffect('phaser', { mix: v / 100 })}
            color="indigo"
          />
          <EffectSlider
            label="Rate"
            value={effects.phaser?.rate}
            onChange={(v) => updateEffect('phaser', { rate: v })}
            min={0.1}
            max={5}
            step={0.1}
            unit="Hz"
            color="indigo"
          />
          <EffectSlider
            label="Depth"
            value={effects.phaser?.depth * 100}
            onChange={(v) => updateEffect('phaser', { depth: v / 100 })}
            color="indigo"
          />
          <EffectSlider
            label="Stages"
            value={effects.phaser?.stages}
            onChange={(v) => updateEffect('phaser', { stages: Math.round(v) })}
            min={2}
            max={12}
            step={2}
            unit=""
            color="indigo"
          />
          <EffectSlider
            label="Feedback"
            value={effects.phaser?.feedback * 100}
            onChange={(v) => updateEffect('phaser', { feedback: v / 100 })}
            color="indigo"
          />
        </EffectSection>

        {/* Distortion */}
        <EffectSection
          title="Distortion"
          icon={Zap}
          color="red"
          enabled={effects.distortion?.enabled}
          onToggle={(v) => updateEffect('distortion', { enabled: v })}
          presets={EFFECT_PRESETS.distortion}
          currentPreset={activePresets.distortion}
          onPresetSelect={(p) => selectPreset('distortion', p)}
          isPremium
          isLocked={isCreativeLocked}
        >
          <EffectSlider
            label="Mix"
            value={effects.distortion?.mix * 100}
            onChange={(v) => updateEffect('distortion', { mix: v / 100 })}
            color="red"
          />
          <EffectSlider
            label="Drive"
            value={effects.distortion?.drive * 100}
            onChange={(v) => updateEffect('distortion', { drive: v / 100 })}
            color="red"
          />
          <EffectSlider
            label="Tone"
            value={effects.distortion?.tone * 100}
            onChange={(v) => updateEffect('distortion', { tone: v / 100 })}
            color="red"
          />
          <EffectSlider
            label="Output"
            value={effects.distortion?.output * 100}
            onChange={(v) => updateEffect('distortion', { output: v / 100 })}
            color="red"
          />
        </EffectSection>
      </div>
    </div>
  );
};

export default AdvancedEffects;
